import { createSlice, current, type PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import type { DoorFrameColorEnum } from 'products/doorframes';
import type { IShoppingProductsMap, IVisibleProducts } from 'utils/constants';
import { isShallowIdentical } from 'utils/isShallowIdentical';

import { fetchProductsData } from './products.thunks';
import type { DoorHeightEnum } from 'CoreAPI/Components/DoorHeightEnum';
import type { IShoppingProduct } from '@inter-ikea-kompis/types';

export interface IMissingSkyttaProducts {
  twoTrackRailColors: DoorFrameColorEnum[];
  threeTrackRailColors: DoorFrameColorEnum[];
  lowDoorColors: DoorFrameColorEnum[];
  highDoorColors: DoorFrameColorEnum[];
}

type State = {
  allProducts: IShoppingProductsMap;
  allValidSPRs: IShoppingProduct[];
  visibleProducts: IVisibleProducts;
  missingSkyttaProducts: IMissingSkyttaProducts;
  doorHeightOptionsAvailable: DoorHeightEnum[];
  isThreeTrackAvailable: boolean;
  activeProductId?: string;
};

const initialState: State = {
  allProducts: {},
  allValidSPRs: [],
  visibleProducts: {},
  missingSkyttaProducts: {
    twoTrackRailColors: [],
    threeTrackRailColors: [],
    lowDoorColors: [],
    highDoorColors: []
  },
  doorHeightOptionsAvailable: [],
  isThreeTrackAvailable: true,
  activeProductId: undefined
};

export const { reducer: productsReducer, actions: productAction } = createSlice({
  name: 'products',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchProductsData.rejected, (state, action) => {
      const error = new Error();
      error.name = 'Failed to fetch product data';
      error.message = action.error.message || '';

      captureException(error, (scope) => {
        scope.setExtra('state', state);
        scope.setExtra('action', action);
        return scope;
      });
    });
  },
  reducers: {
    setProducts: (state, { payload: allProducts }: PayloadAction<State['allProducts']>) => {
      if (isShallowIdentical(current(state).allProducts, allProducts)) {
        return;
      }

      return {
        ...state,
        allProducts
      };
    },
    setAllValidSPRs: (state, { payload: allValidSPRs }: PayloadAction<State['allValidSPRs']>) => {
      if (isShallowIdentical(current(state).allValidSPRs, allValidSPRs)) {
        return;
      }

      return {
        ...state,
        allValidSPRs
      };
    },
    setVisibleProducts: (state, { payload: visibleProducts }: PayloadAction<State['visibleProducts']>) => {
      if (isShallowIdentical(current(state).visibleProducts, visibleProducts)) {
        return;
      }

      return { ...state, visibleProducts };
    },
    setActiveProductId: (state, { payload: activeProductId }: PayloadAction<State['activeProductId']>) => {
      if (isShallowIdentical(current(state).activeProductId, activeProductId)) {
        return;
      }

      return { ...state, activeProductId };
    },
    setMissingSkyttaProducts: (state, { payload: missingSkyttaProducts }: PayloadAction<State['missingSkyttaProducts']>) => {
      if (isShallowIdentical(current(state).missingSkyttaProducts, missingSkyttaProducts)) {
        return;
      }

      return { ...state, missingSkyttaProducts };
    },
    setDoorHeightOptionsAvailable: (state, { payload: doorHeightOptionsAvailable }: PayloadAction<State['doorHeightOptionsAvailable']>) => {
      if (isShallowIdentical(current(state).doorHeightOptionsAvailable, doorHeightOptionsAvailable)) {
        return;
      }

      return { ...state, doorHeightOptionsAvailable };
    },
    setIsThreeTrackAvailable: (state, { payload: isThreeTrackAvailable }: PayloadAction<State['isThreeTrackAvailable']>) => {
      if (current(state).isThreeTrackAvailable === isThreeTrackAvailable) {
        return;
      }

      return { ...state, isThreeTrackAvailable };
    }
  }
});
